// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { TourKind } from "../dto/tourModels";

const STORAGE_PREFIX = "riddle-tour";

export const TUTORIAL_STORAGE_KEYS = {
  onboardingDone: `${STORAGE_PREFIX}-onboarding-done`,
  workspaceDone: `${STORAGE_PREFIX}-workspace-done`,
  dismissedSession: `${STORAGE_PREFIX}-dismissed-session`,
} as const;

function doneKey(kind: TourKind): string {
  return kind === "onboarding"
    ? TUTORIAL_STORAGE_KEYS.onboardingDone
    : TUTORIAL_STORAGE_KEYS.workspaceDone;
}

function readFlag(key: string): boolean {
  return localStorage.getItem(key) === "true";
}

export interface TutorialContextType {
  isActive: boolean;
  tour: TourKind | null;
  start: (kind: TourKind) => void;
  dismiss: () => void;
  finish: (kind: TourKind) => void;
  canAutoStart: (kind: TourKind) => boolean;
}

const TutorialContext = createContext<TutorialContextType | undefined>(
  undefined,
);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [activeTour, setActiveTour] = useState<TourKind | null>(null);

  const start = useCallback((kind: TourKind) => {
    setActiveTour(kind);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(TUTORIAL_STORAGE_KEYS.dismissedSession, "true");
    setActiveTour(null);
  }, []);

  const finish = useCallback((kind: TourKind) => {
    localStorage.setItem(doneKey(kind), "true");
    setActiveTour(null);
  }, []);

  const canAutoStart = useCallback((kind: TourKind) => {
    if (readFlag(doneKey(kind))) {
      return false;
    }
    if (localStorage.getItem(TUTORIAL_STORAGE_KEYS.dismissedSession) === "true") {
      return false;
    }
    return true;
  }, []);

  const value = useMemo<TutorialContextType>(
    () => ({
      isActive: activeTour !== null,
      tour: activeTour,
      start,
      dismiss,
      finish,
      canAutoStart,
    }),
    [activeTour, start, dismiss, finish, canAutoStart],
  );

  return (
    <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>
  );
}

export function useTutorial(): TutorialContextType {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("useTutorial must be used within TutorialProvider");
  }
  return context;
}
